package com.arenaproplay.admin.ui.components.admin

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import com.arenaproplay.admin.ui.components.icons.LucideIcon
import com.arenaproplay.admin.ui.theme.bodySMedium
import com.arenaproplay.admin.ui.utils.sBorderRadius
import kotlinx.browser.document
import org.jetbrains.compose.web.css.*
import org.w3c.dom.HTMLAnchorElement
import org.w3c.dom.url.URL
import org.w3c.files.Blob
import org.w3c.files.BlobPropertyBag

private fun csvEscape(value: String): String {
    val needsQuotes = value.any { it == ',' || it == '"' || it == '\n' || it == '\r' }
    return if (needsQuotes) "\"${value.replace("\"", "\"\"")}\"" else value
}

/** Builds a CSV in memory and triggers a browser download. */
fun downloadCsv(filename: String, header: List<String>, rows: List<List<String>>) {
    val body = buildString {
        // BOM so Excel opens UTF-8 (Cyrillic names etc.) correctly.
        append('\uFEFF')
        (listOf(header) + rows).forEach { row ->
            append(row.joinToString(",") { csvEscape(it) })
            append("\r\n")
        }
    }
    val blob = Blob(arrayOf(body), BlobPropertyBag(type = "text/csv;charset=utf-8;"))
    val url = URL.createObjectURL(blob)
    val anchor = document.createElement("a") as HTMLAnchorElement
    anchor.href = url
    anchor.download = filename
    document.body?.appendChild(anchor)
    anchor.click()
    anchor.remove()
    URL.revokeObjectURL(url)
}

@Composable
fun ExportCsvButton(
    exporting: Boolean,
    modifier: Modifier = Modifier,
    label: String = "Export CSV",
    onClick: () -> Unit,
) {
    Box(
        modifier
            .sBorderRadius(999)
            .padding(leftRight = 16.px, topBottom = 9.px)
            .cursor(if (exporting) Cursor.Progress else Cursor.Pointer)
            .styleModifier {
                property("background", "#0B1220")
                property("border", "1px solid rgba(255,255,255,0.06)")
                property("box-shadow", "0 1px 2px rgba(0,0,0,0.03)")
                property("transition", "opacity 140ms ease")
                if (exporting) property("opacity", "0.55")
            }
            .onClick { if (!exporting) onClick() },
    ) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            LucideIcon(name = "download", size = 14, color = "#A7ADB9")
            Box(Modifier.width(8.px))
            SpanText(
                if (exporting) "Exporting…" else label,
                Modifier.bodySMedium().color(Color("#F0F4FC"))
                    .styleModifier { property("font-weight", "600") },
            )
        }
    }
}

/**
 * Pulls every page of a paginated endpoint (largest page size the backend
 * allows) so the export covers the whole filtered set, not just the visible
 * page. Capped to keep a runaway total from freezing the tab.
 */
suspend fun <T> fetchAllPages(
    maxRows: Int = 10_000,
    fetch: suspend (page: Int, pageSize: Int) -> com.arenaproplay.admin.domain.admin.PageResult<T>,
): List<T> {
    val pageSize = 200
    val all = mutableListOf<T>()
    var page = 0
    while (true) {
        val result = fetch(page, pageSize)
        all += result.items
        val loaded = (page + 1) * result.pageSize
        if (result.items.isEmpty() || loaded >= result.total || all.size >= maxRows) break
        page++
    }
    return all
}
